import React, { useMemo, useState } from "react";
import axios from "axios";
import { compressImageForUpload } from "../utils/uploadCompression";
import { rowsFromTemplate, templatesForSubject, viewsFromPaper } from "../utils/sectionTemplates";
import "./SectionSetupHelper.css";

// Line icons in the app's stroke style.
const icon = (paths) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {paths}
  </svg>
);
const CHOICES = [
  {
    id: "memo",
    icon: icon(<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M12 18v-6" /><path d="m9 15 3-3 3 3" /></>),
    title: "Read it from my memo",
    text: "Upload your memo or question paper. MarkSure finds the questions and their marks.",
    badge: "Easiest",
  },
  {
    id: "template",
    icon: icon(<><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /></>),
    title: "Use a common layout",
    text: "Start from how papers in this subject are usually set out.",
  },
  {
    id: "whole",
    icon: icon(<><rect x="3" y="4" width="18" height="16" /><path d="M8 12h8" /></>),
    title: "One mark for the whole paper",
    text: "Just capture each learner's total. You can always split it later.",
  },
];

/**
 * Guided set-up for an assessment's sections: from the memo, a common layout or one total.
 * The teacher always sees and can edit the rows before anything is added.
 */
export default function SectionSetupHelper({ subjectName, grade, categories = [], existingGroups = [], onCreate }) {
  const [mode, setMode] = useState(null);
  const [assessmentName, setAssessmentName] = useState("");
  const [weight, setWeight] = useState(1);
  const [category, setCategory] = useState("");
  const [total, setTotal] = useState(50);
  const [rows, setRows] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [questionCount, setQuestionCount] = useState(4);
  const [memo, setMemo] = useState({ loading: false, error: "", result: null, view: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const templates = useMemo(() => templatesForSubject(subjectName, grade), [subjectName, grade]);
  const existing = assessmentName.trim()
    ? existingGroups.find((group) => group.name.trim().toLowerCase() === assessmentName.trim().toLowerCase())
    : null;
  const rowTotal = rows.reduce((acc, row) => acc + (Number(row.max) || 0), 0);

  const reset = () => {
    setMode(null); setRows([]); setTemplateId(""); setError("");
    setMemo({ loading: false, error: "", result: null, view: "" });
  };

  const choose = (id) => {
    setMode(id); setError("");
    if (id === "whole") setRows([{ name: "Whole paper", max: total }]);
    if (id === "template") pickTemplate(templates[0], total, questionCount);
    if (id === "memo") setRows([]);
  };

  const pickTemplate = (template, nextTotal, count) => {
    if (!template) return;
    setTemplateId(template.id);
    setRows(rowsFromTemplate(template, nextTotal, count));
  };

  const changeTotal = (value) => {
    setTotal(value);
    if (mode === "whole") setRows([{ name: rows[0]?.name || "Whole paper", max: value }]);
    if (mode === "template") pickTemplate(templates.find((t) => t.id === templateId), value, questionCount);
  };

  const readMemo = async (file) => {
    if (!file) return;
    setMemo({ loading: true, error: "", result: null, view: "" });
    try {
      const upload = await compressImageForUpload(file);
      const data = new FormData();
      data.append("paper", upload, upload.name);
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/suggest_sections.php`, data, { timeout: 120000 });
      const result = viewsFromPaper(response.data);
      if (result.total <= 0) throw new Error("No marks were found in this file.");
      setMemo({ loading: false, error: "", result, view: result.defaultView });
      setRows(result.views.find((view) => view.id === result.defaultView).rows);
      setTotal(result.total);
    } catch (requestError) {
      const message = requestError?.response?.data?.error || requestError?.message || "The memo could not be read.";
      setMemo({ loading: false, error: message, result: null, view: "" });
    }
  };

  const showMemoView = (view) => {
    setMemo((current) => ({ ...current, view: view.id }));
    setRows(view.rows);
  };

  const updateRow = (index, field, value) =>
    setRows((current) => current.map((row, i) => (i === index ? { ...row, [field]: value } : row)));

  const submit = async () => {
    const cleanRows = rows.map((row) => ({ name: String(row.name || "").trim(), max: Number(row.max) })).filter((row) => row.name);
    if (!assessmentName.trim()) return setError("Give the assessment a name, for example 'Term 2 Test'.");
    if (!cleanRows.length) return setError("Add at least one section.");
    if (cleanRows.some((row) => !(row.max > 0))) return setError("Every section needs a max mark above 0.");
    setSaving(true); setError("");
    try {
      await onCreate(cleanRows, {
        assessment_group: assessmentName.trim(),
        weight: existing ? existing.weight : Number(weight) || 1,
        category,
      });
      setAssessmentName("");
      reset();
    } catch {
      setError("The sections could not be added. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="ssh" aria-labelledby="ssh-title">
      <header className="ssh-head">
        <div>
          <h3 id="ssh-title">Not sure how to split up your paper?</h3>
          <p>
            Each section becomes a column on your mark sheet, so you can see which part of the paper learners
            found hard. Let MarkSure set it up, then check it before it's added.
          </p>
        </div>
        {mode && <button type="button" className="ssh-link" onClick={reset}>Start over</button>}
      </header>

      {!mode && (
        <div className="ssh-choices">
          {CHOICES.map((choice) => (
            <button key={choice.id} type="button" className="ssh-choice" onClick={() => choose(choice.id)}>
              <span className="ssh-choice-icon" aria-hidden="true">{choice.icon}</span>
              <span className="ssh-choice-body">
                <strong>{choice.title}{choice.badge && <em>{choice.badge}</em>}</strong>
                <span>{choice.text}</span>
              </span>
            </button>
          ))}
        </div>
      )}

      {mode && (
        <div className="ssh-panel">
          <div className="ssh-fields">
            <label>
              Assessment name
              <input className="modern-input" value={assessmentName} placeholder="e.g. Term 2 Test"
                onChange={(event) => setAssessmentName(event.target.value)} />
            </label>
            <label>
              Overall weight
              <input className="modern-input" type="number" min="0" value={existing ? existing.weight : weight}
                disabled={Boolean(existing)} onChange={(event) => setWeight(event.target.value)} />
              {existing && <small>Same as the existing "{existing.name}"</small>}
            </label>
            <label>
              Category
              <select className="modern-input" value={category} onChange={(event) => setCategory(event.target.value)}>
                {categories.map((c) => <option key={c} value={c}>{c || "None (optional)"}</option>)}
              </select>
            </label>
            {mode !== "memo" && (
              <label>
                Paper total
                <input className="modern-input" type="number" min="1" value={total}
                  onChange={(event) => changeTotal(event.target.value)} />
              </label>
            )}
          </div>

          {mode === "memo" && !memo.result && (
            <div className="ssh-upload">
              {memo.loading ? (
                <p className="ssh-status" role="status"><span className="ssh-spinner" aria-hidden="true" /> Reading your paper. This takes a few seconds…</p>
              ) : (
                <label className="ssh-drop">
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={(event) => readMemo(event.target.files?.[0])} />
                  <strong>Choose your memo or question paper</strong>
                  <span>PDF or a photo. Use the same memo when you AI-mark the scripts.</span>
                </label>
              )}
              {memo.error && <p className="ssh-error" role="alert">{memo.error}</p>}
            </div>
          )}

          {mode === "memo" && memo.result && (
            <div className="ssh-found">
              <p>
                Your paper is out of <strong>{memo.result.total} marks</strong>.{" "}
                {memo.result.views.length > 1
                  ? "Choose how to split it:"
                  : "Its parts didn't add up to the total, so it's set up as one mark. Add sections below if you like."}
                {memo.result.tooMany && memo.view === "whole" && " It has a lot of separate questions, so one mark for the whole paper keeps your mark sheet tidy."}
              </p>
              {memo.result.views.length > 1 && (
                <div className="ssh-toggle" role="group" aria-label="How to split the paper">
                  {memo.result.views.map((view) => (
                    <button key={view.id} type="button" className={memo.view === view.id ? "selected" : ""} onClick={() => showMemoView(view)}>
                      {view.label}{view.id !== "whole" && ` (${view.rows.length})`}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {mode === "template" && (
            <div className="ssh-templates" role="radiogroup" aria-label="Layout">
              {templates.map((template) => (
                <button key={template.id} type="button" role="radio" aria-checked={templateId === template.id}
                  className={`ssh-template ${templateId === template.id ? "selected" : ""}`}
                  onClick={() => pickTemplate(template, total, questionCount)}>
                  <strong>{template.label}</strong>
                  <span>{template.description}</span>
                </button>
              ))}
              {templates.find((t) => t.id === templateId)?.questionCount && (
                <label className="ssh-count">
                  Number of questions
                  <input className="modern-input" type="number" min="1" max="30" value={questionCount}
                    onChange={(event) => {
                      setQuestionCount(event.target.value);
                      pickTemplate(templates.find((t) => t.id === templateId), total, Number(event.target.value));
                    }} />
                </label>
              )}
            </div>
          )}

          {rows.length > 0 && (
            <div className="ssh-rows">
              <div className="ssh-rows-head"><span>Section</span><span>Max mark</span><span /></div>
              {rows.map((row, index) => (
                <div className="ssh-row" key={index}>
                  <input className="modern-input" aria-label={`Section ${index + 1} name`} value={row.name}
                    onChange={(event) => updateRow(index, "name", event.target.value)} />
                  <input className="modern-input" type="number" min="0" aria-label={`Section ${index + 1} max mark`} value={row.max}
                    onChange={(event) => updateRow(index, "max", event.target.value)} />
                  <button type="button" className="ssh-remove" aria-label={`Remove ${row.name || "section"}`}
                    onClick={() => setRows((current) => current.filter((_, i) => i !== index))}>×</button>
                </div>
              ))}
              <div className="ssh-rows-foot">
                <button type="button" className="ssh-link" onClick={() => setRows((current) => [...current, { name: `Question ${current.length + 1}`, max: 5 }])}>
                  + Add a section
                </button>
                <span className={mode !== "memo" && Number(total) > 0 && rowTotal !== Number(total) ? "ssh-mismatch" : ""}>
                  Total: <strong>{rowTotal}</strong>
                  {mode !== "memo" && Number(total) > 0 && rowTotal !== Number(total) && ` (paper is out of ${total})`}
                </span>
              </div>
            </div>
          )}

          {error && <p className="ssh-error" role="alert">{error}</p>}
          <div className="ssh-actions">
            <button type="button" className="ssh-link" onClick={reset}>Cancel</button>
            <button type="button" className="btn-add-section" disabled={saving || !rows.length} onClick={submit}>
              {saving ? "Adding…" : `Add ${rows.length} section${rows.length === 1 ? "" : "s"} to the mark sheet`}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
